import PropTypes from 'prop-types';
import { Form } from 'react-final-form';
import styled from '@emotion/styled/macro';
import { useRef, useCallback } from 'react';
import { colors } from '../../styles/theme';
import { Button } from '../../form';
import { CancelLinkButton } from '../../form/buttons';
import CommentTextarea from './CommentTextarea';
import validate, { isRequired } from '../validation';

// Helper function to extract mentioned user IDs from text
const extractMentionedUserIds = (text, mentionMap) => {
  const mentionPattern = /\*\*([^*]+)\*\*/g;
  const currentMentions = [];
  let match;
  while ((match = mentionPattern.exec(text)) !== null) {
    currentMentions.push(match[1].trim());
  }
  return currentMentions
    .map((display) => mentionMap.get(display))
    .filter((id) => id !== undefined);
};

const borderStyle = `1px solid ${colors.grey.secondaryDark};`;
const annotationLeftBorderStyle = `3px solid ${colors.orange.primary};`;

const CommentReplyFormContainer = styled('div')`
  padding: 20px 18px;
  ${(props) => props.borderBottom && `border-bottom: ${borderStyle}`}
  ${(props) =>
    props.borderSides &&
    `
    border-right: ${borderStyle}
    border-left: ${borderStyle}
  `}
  background: ${colors.grey.secondaryLight};
  border-radius: 0 0 3px 3px;
  ${(props) => props.borderLeft && `border-left: ${annotationLeftBorderStyle}`}
`;

const Actions = styled('div')`
  text-align: right;
  margin-top: 11px;
`;

const StyledCancelButton = styled(CancelLinkButton)`
  line-height: 30px;
  height: auto;
  text-transform: uppercase;
`;

function ReplyForm({ body, onSubmit, onCancel, borderBottom, borderSides, users, borderLeft }) {
  // Track mentioned user IDs as they're selected from dropdown
  const mentionMapRef = useRef(new Map());

  const handleMentionAdd = useCallback((id, display) => {
    mentionMapRef.current.set(display, Number(id));
  }, []);

  const handleSubmitWithMentions = (data) => {
    const mentionedUserIds = extractMentionedUserIds(data.reply || '', mentionMapRef.current);
    onSubmit({ ...data, mentionedUserIds });
    mentionMapRef.current.clear();
  };

  return (
    <CommentReplyFormContainer
      borderBottom={borderBottom}
      borderSides={borderSides}
      borderLeft={borderLeft}
    >
      <Form
        initialValues={{ body }}
        onSubmit={handleSubmitWithMentions}
        render={({ handleSubmit, pristine, form }) => (
          <form
            onSubmit={(event) => {
              handleSubmit(event);
              form.reset();
            }}
          >
            <CommentTextarea
              name="reply"
              placeholder="Reply..."
              rows={1}
              validate={validate(isRequired)}
              users={users}
              onMentionAdd={handleMentionAdd}
            />
            <Actions>
              <StyledCancelButton
                href="/"
                size="small"
                onClick={(e) => {
                  onCancel(e);
                }}
              />
              <Button type="submit" variant="secondary" size="small" disabled={pristine}>
                Reply
              </Button>
            </Actions>
          </form>
        )}
      />
    </CommentReplyFormContainer>
  );
}

const userShape = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  display: PropTypes.string.isRequired,
  avatar: PropTypes.string.isRequired,
};

ReplyForm.defaultProps = {
  borderTop: null,
  borderBottom: null,
  borderSides: null,
  users: [],
  annotation: null,
};

ReplyForm.propTypes = {
  body: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  borderTop: PropTypes.bool,
  borderBottom: PropTypes.bool,
  borderSides: PropTypes.bool,
  users: PropTypes.arrayOf(PropTypes.shape(userShape)),
  annotation: PropTypes.bool,
};

function EditForm({ body, onSubmit, onCancel, users }) {
  // Track mentioned user IDs as they're selected from dropdown
  const mentionMapRef = useRef(new Map());

  const handleMentionAdd = useCallback((id, display) => {
    mentionMapRef.current.set(display, Number(id));
  }, []);

  const handleSubmitWithMentions = (data) => {
    const mentionedUserIds = extractMentionedUserIds(data.edit || '', mentionMapRef.current);
    onSubmit({ ...data, mentionedUserIds });
    mentionMapRef.current.clear();
  };

  return (
    <>
      <Form
        initialValues={{ body }}
        onSubmit={handleSubmitWithMentions}
        render={({ handleSubmit, invalid }) => (
          <form>
            <CommentTextarea
              name="edit"
              value={body}
              placeholder={false}
              rows={1}
              validate={validate(isRequired)}
              users={users}
              onMentionAdd={handleMentionAdd}
            />
            <Actions>
              <StyledCancelButton
                href="/"
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  onCancel();
                }}
              />
              <Button
                type="submit"
                variant="secondary"
                size="small"
                onClick={handleSubmit}
                disabled={invalid}
              >
                Update
              </Button>
            </Actions>
          </form>
        )}
      />
    </>
  );
}

EditForm.propTypes = {
  body: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  users: PropTypes.arrayOf(PropTypes.shape(userShape)),
};

export { ReplyForm, EditForm };
